import { useState } from 'react'
import type { AllocationInfo } from '@/types/api'
import { Card } from '@/components/shared/Card'
import { Button } from '@/components/shared/Button'
import { CapProgressBar } from './CapProgressBar'
import { StatusBadge } from './StatusBadge'
import { RequestCapModal } from './RequestCapModal'
import { formatCap, formatEta } from '@/utils/formatters'
import { useDashboard } from '@/hooks/useDashboard'

interface GeoCardProps {
  allocation: AllocationInfo
  readOnly?: boolean
  /** Set when the selected offer is admin-paused — the server refuses new cap_requests
   *  while true, so Request Cap is disabled here instead of only surfacing the 409
   *  after submit. */
  paused?: boolean
}

export function GeoCard({ allocation, readOnly, paused = false }: GeoCardProps) {
  const [showRequestCap, setShowRequestCap] = useState(false)
  const { capUnit } = useDashboard()
  const fmt = (n: number) => formatCap(n, capUnit)

  // Encroachment is computed server-side using actual usage from all users
  const encroachment = allocation.encroachment ?? 0
  const privateCap = allocation.private_cap ?? 0
  const used = allocation.used ?? 0
  const effectiveCap = Math.max(used, privateCap - encroachment)
  const remaining = Math.max(0, effectiveCap - used)
  const ratePerHour = allocation.rate_per_hour ?? 0

  const myPaceText = ratePerHour > 0
    ? `${fmt(ratePerHour)}/hr`
    : remaining > 0 ? 'Not expected to fill' : '—'
  const capEta = ratePerHour > 0 ? formatEta(remaining, ratePerHour) : '—'

  return (
    <Card className="animate-fade-in flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-gray-50 border border-gray-200 flex items-center justify-center">
            <span className="font-semibold text-sm text-text-primary">{allocation.geo_code}</span>
          </div>
          <div>
            <h3 className="font-semibold text-sm text-text-primary">{allocation.geo_name}</h3>
            <p className="text-xs text-text-muted mt-0.5">
              {fmt(remaining)} remaining
            </p>
          </div>
        </div>
        <StatusBadge status={allocation.status} />
      </div>

      {/* My Cap */}
      <div className="mb-4">
        <CapProgressBar
          type="personal"
          used={used}
          total={privateCap}
          othersUsed={encroachment > 0 ? encroachment : undefined}
          effectiveTotal={encroachment > 0 ? effectiveCap : undefined}
        />
        <div className="flex items-center justify-between mt-2 text-xs">
          <div>
            <span className="text-text-muted">Pace </span>
            <span className="font-mono font-medium text-text-secondary">{myPaceText}</span>
          </div>
          <div>
            <span className="text-text-muted">ETA </span>
            <span className="font-mono font-medium text-text-secondary">{capEta}</span>
          </div>
        </div>
        {encroachment > 0 && (
          <p className="text-[10px] text-brand-red mt-1.5 font-medium">
            Someone else is using {fmt(encroachment)} of your geo's cap — notify admin
          </p>
        )}
      </div>

      {/* Historical Averages — anchored to bottom */}
      <div className="flex items-center gap-3 py-2.5 px-3 mb-4 rounded-lg bg-gray-50 text-xs mt-auto">
        <div>
          <span className="text-text-placeholder">7d</span>
          <span className="ml-1 font-mono font-medium text-text-secondary">{fmt(allocation.avg_daily_7d ?? 0)}/d</span>
        </div>
        <div className="w-px h-3 bg-gray-200" />
        <div>
          <span className="text-text-placeholder">3d</span>
          <span className="ml-1 font-mono font-medium text-text-secondary">{fmt(allocation.avg_daily_3d ?? 0)}/d</span>
        </div>
        <div className="w-px h-3 bg-gray-200" />
        <div>
          <span className="text-text-placeholder">Yesterday</span>
          <span className="ml-1 font-mono font-medium text-text-secondary">{fmt(allocation.avg_daily_1d ?? 0)}</span>
        </div>
      </div>

      {/* Actions — hidden in readOnly mode */}
      {!readOnly && (
        <>
          <div className="border-t border-gray-100 my-1 mb-5" />
          <Button
            variant="primary"
            disabled={paused}
            onClick={() => setShowRequestCap(true)}
            className="w-full text-xs"
          >
            Request Cap
          </Button>
          {paused && <p className="text-[10px] text-amber-600 text-center mt-1">Offer is paused</p>}
          <RequestCapModal
            open={showRequestCap}
            onClose={() => setShowRequestCap(false)}
            geoId={allocation.geo_id}
            geoCode={allocation.geo_code}
            geoName={allocation.geo_name}
            unit={capUnit}
          />
        </>
      )}
    </Card>
  )
}
